/**
 * Your stops, in localStorage.
 *
 * There is no server and no account, so this file is the entire persistence
 * layer. It is also the reason the board can paint immediately: every saved
 * pair carries its line number and destination text, so the screen is fully
 * laid out before SL has answered and only the times arrive late.
 */
import { useSyncExternalStore } from 'react';
import type { Pair } from './sl.ts';

const KEY = 'sl:config:1';

export type SavedStop = {
  siteId: number;
  /** Snapshotted at save time so the board never waits on the stop list. */
  name: string;
  note?: string;
  /**
   * "Stocksund, Danderyd". Only ever set on stops whose name is not unique,
   * which is what makes it safe to display unconditionally -- it shows up
   * precisely when there is another stop it could be confused with, and
   * stays out of the way otherwise.
   */
  area?: string;
  /**
   * Hidden on the board until you open it again. Saved rather than held in
   * memory, because a stop you have finished with for the day should stay
   * shut when you come back to the app twenty minutes later.
   */
  collapsed?: boolean;
  pairs: Pair[];
};

export type Config = { stops: SavedStop[] };

const EMPTY: Config = { stops: [] };

function read(): Config {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return EMPTY;
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== 'object') return EMPTY;
    const stops = (parsed as Config).stops;
    // Hand-edited or half-written storage should degrade to "no stops yet"
    // rather than throwing on every render.
    if (!Array.isArray(stops)) return EMPTY;
    return { stops: stops.filter(isStop) };
  } catch {
    return EMPTY;
  }
}

function isStop(s: unknown): s is SavedStop {
  if (!s || typeof s !== 'object') return false;
  const v = s as SavedStop;
  return (
    typeof v.siteId === 'number' && typeof v.name === 'string' && Array.isArray(v.pairs)
  );
}

// useSyncExternalStore compares snapshots by identity, so it has to be the same
// object until something actually changes -- parsing on every call would spin.
let cache: Config | null = null;
const listeners = new Set<() => void>();

function snapshot(): Config {
  cache ??= read();
  return cache;
}

function emit() {
  cache = null;
  for (const fn of listeners) fn();
}

function subscribe(fn: () => void) {
  listeners.add(fn);
  // Another tab (or the setup screen in a second window) writing the same key.
  const onStorage = (e: StorageEvent) => {
    if (e.key === KEY) emit();
  };
  window.addEventListener('storage', onStorage);
  return () => {
    listeners.delete(fn);
    window.removeEventListener('storage', onStorage);
  };
}

function write(next: Config) {
  try {
    localStorage.setItem(KEY, JSON.stringify(next));
  } catch {
    // Private mode, or a full quota. Keep the in-memory value so the session
    // still works; it just will not survive a reload.
  }
  cache = next;
  for (const fn of listeners) fn();
}

/** The saved config, re-rendering whatever reads it when it changes. */
export function useConfig(): Config {
  return useSyncExternalStore(subscribe, snapshot, () => EMPTY);
}

export function saveStop(stop: SavedStop) {
  const { stops } = snapshot();
  const i = stops.findIndex((s) => s.siteId === stop.siteId);
  // Editing an existing stop keeps its position in the list; a new one goes
  // to the end, where you just added it.
  const next = i === -1 ? [...stops, stop] : stops.with(i, stop);
  write({ stops: next });
}

export function setStopCollapsed(siteId: number, collapsed: boolean) {
  const { stops } = snapshot();
  const i = stops.findIndex((s) => s.siteId === siteId);
  const stop = stops[i];
  if (!stop || Boolean(stop.collapsed) === collapsed) return;
  write({ stops: stops.with(i, { ...stop, collapsed }) });
}

export function removeStop(siteId: number) {
  write({ stops: snapshot().stops.filter((s) => s.siteId !== siteId) });
}

export function moveStop(siteId: number, delta: -1 | 1) {
  const { stops } = snapshot();
  const i = stops.findIndex((s) => s.siteId === siteId);
  const j = i + delta;
  if (i === -1 || j < 0 || j >= stops.length) return;
  const next = [...stops];
  const [moved] = next.splice(i, 1);
  if (moved) next.splice(j, 0, moved);
  write({ stops: next });
}

export const findStop = (config: Config, siteId: number) =>
  config.stops.find((s) => s.siteId === siteId);

export type { Pair };
